/**
 * Feature: Roofing CRM
 * /features/crm
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { Users, Target, Network, RefreshCw, Star, BarChart3, ArrowRight } from 'lucide-react';
import { ProductShowcase } from '@/components/marketing/ProductShowcase';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'What makes Rooflyzer CRM different from HubSpot or Salesforce?',
    answer:
      'Those tools are built for B2B software companies. Rooflyzer CRM is built around roofing workflows: storm leads, insurance claims, canvassing attribution, and re-nurture sequences specific to the contracting business cycle.',
  },
  {
    question: 'How does lead scoring work?',
    answer:
      'Rooflyzer automatically scores leads based on property value, storm proximity, engagement history, and deal size. You can also set manual scores and create custom scoring rules.',
  },
  {
    question: 'Can I import contacts from my current CRM?',
    answer:
      'Yes. Import contacts from any CRM via CSV. We also offer white-glove migration for Scale plan customers.',
  },
  {
    question: 'How many contacts can I store?',
    answer: 'Unlimited on Pro and Scale plans. Free plan allows up to 500 contacts.',
  },
  {
    question: 'Does the CRM work on mobile?',
    answer:
      'Yes. Fully mobile — view contacts, log calls, update pipeline stages, and send follow-ups from your phone.',
  },
];

const crmStats = [
  { value: '40+', label: 'Re-nurture reason codes', desc: 'Built for roofing' },
  { value: '360°', label: 'Customer view', desc: 'From first contact to referral' },
  { value: '5 stages', label: 'Pipeline', desc: 'From new lead to closed job' },
  { value: 'Real-time', label: 'Rep performance', desc: 'And close rate analytics' },
];

export const CRMPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Roofing CRM',
    description:
      'A CRM purpose-built for roofing contractors. Track every lead, automate follow-ups, score prospects, and build customer relationships that generate referrals.',
    featureList: [
      '360° Contact Profiles',
      'Lead Scoring & Prioritization',
      'Lifecycle Stage Tracking',
      '40+ Re-Nurture Reason Codes',
      'Referral Source Tracking',
      'Sales Performance Analytics',
    ],
    url: 'https://rooflyzer.pro/features/crm',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing CRM — Manage Every Lead, Contact & Customer"
        description="A CRM purpose-built for roofing contractors. Track every lead, automate follow-ups, score prospects, and build customer relationships that generate referrals."
        canonicalUrl="/features/crm"
        ogImage="/og/pipeline.png"
        keywords="roofing CRM, roofing customer relationship management, roofing lead management software, CRM for roofers"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'CRM & Contacts', url: '/features/crm' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={Users}>Your Customer Database</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              The CRM That <span className="text-[#C29BF8]">Speaks Roofing</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Every lead, every customer, every interaction — organized in a CRM built around how
              roofing businesses actually sell. Track storm leads differently from referrals. Score
              insurance jobs differently from retail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold px-8 shadow-md shadow-[#7950A2]/20"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Free CRM
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/features">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See All CRM Features
                </Button>
              </Link>
            </div>
          </section>

          {/* Product film */}
          <section className="mb-24">
            <ProductShowcase frame={PRODUCT_FRAMES.pipeline} float={false} />
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Built for the Roofing Sales Cycle
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: Users,
                  title: '360° Contact Profiles',
                  desc: "Every contact has a complete history: calls, emails, estimates, jobs, payments, and notes. See a customer's full lifecycle at a glance.",
                },
                {
                  icon: Target,
                  title: 'Lead Scoring & Prioritization',
                  desc: 'Automatically rank leads by deal size, storm zone, property value, and engagement. Work the hottest leads first.',
                },
                {
                  icon: Network,
                  title: 'Lifecycle Stage Tracking',
                  desc: 'Move leads through: New → Contacted → Inspected → Proposal Sent → Negotiating → Closed Won/Lost. Never lose track of where a deal stands.',
                },
                {
                  icon: RefreshCw,
                  title: '40+ Re-Nurture Reason Codes',
                  desc: 'Tag dormant leads with specific re-engagement reasons. Run targeted campaigns to the right segments at the right time.',
                },
                {
                  icon: Star,
                  title: 'Referral Source Tracking',
                  desc: 'Know exactly where every lead came from — and which sources close best. Double down on what works.',
                },
                {
                  icon: BarChart3,
                  title: 'Sales Performance Analytics',
                  desc: 'Rep leaderboards, close rate by lead source, average deal size, and pipeline velocity — all in real time.',
                },
              ]}
            />
          </section>

          {/* The Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    "Generic CRM with custom fields that don't make sense for roofing",
                    'No concept of storms or insurance in your pipeline',
                    'Leads fall through the cracks without structured follow-up',
                    'No re-nurture strategy — dormant leads just go cold forever',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Rooflyzer CRM</h2>
                <FeatureChecklist
                  items={[
                    'CRM built around the roofing sales cycle from day one',
                    'Storm-aware lead classification built into every pipeline stage',
                    '40+ re-nurture codes for targeted re-engagement campaigns',
                    'Automated follow-ups so no lead goes cold without a touchpoint',
                    'Rep performance dashboards showing close rate and pipeline health',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="By the Numbers" items={crmStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Ready to Build Real Customer Relationships?"
            buttonLabel="Start Free — No Credit Card"
          >
            Start with a free account. No credit card. No contract. Just a CRM that actually works
            for roofing.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default CRMPage;
